"use client";

import {
  AlertCircle,
  Loader2,
  Music2,
  PenLine,
  Plus,
  UserRound,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import { HlsVideo } from "@/components/ui/hls-video";
import { clampFeedMediaAspectRatio } from "@/lib/aspect-ratios";
import type { MusicAttachment } from "@/lib/music/attachment";
import { cn } from "@/lib/utils";

/**
 * Composer-only pieces of the X-style create-post sheet. Each one renders the
 * attachment the way the published `Post` will (same frame, radius, border and
 * column geometry), plus the edit/remove affordances a draft needs.
 */

/** Feed geometry: px-3 (12) + 40px avatar + gap-2.5 (10). */
export const COMPOSER_COLUMN_BLEED = "-mr-3 -ml-[62px]";
const COMPOSER_COLUMN_PAD = "pr-3 pl-[62px]";

export type ComposerMediaItem = {
  id: string;
  kind: "image" | "video";
  provider: "r2" | "klipy";
  url: string | null;
  posterUrl?: string | null;
  width?: number;
  height?: number;
  isUploading?: boolean;
  error?: string;
  label: string;
  /** Uploaded stills can carry people tags. */
  canTag: boolean;
  tagCount: number;
};

function MediaFill({
  item,
  sizes,
}: {
  item: ComposerMediaItem;
  sizes: string;
}) {
  if (!item.url) {
    return <div className="h-full w-full bg-surface-alt" />;
  }
  if (item.provider === "klipy") {
    return item.kind === "video" ? (
      <video
        src={item.url}
        className="h-full w-full object-cover"
        playsInline
        muted
        loop
        autoPlay
      />
    ) : (
      // biome-ignore lint/performance/noImgElement: animated KLIPY media must stay animated.
      <img
        src={item.url}
        alt={item.label}
        draggable={false}
        className="h-full w-full object-cover"
      />
    );
  }
  if (item.kind === "video") {
    return (
      <HlsVideo
        autoPlay
        loop
        muted
        playsInline
        src={item.url}
        poster={item.posterUrl ?? undefined}
        className="h-full w-full object-cover"
      />
    );
  }
  return (
    <Image
      src={item.url}
      alt={item.label}
      fill
      className="object-cover"
      sizes={sizes}
    />
  );
}

/** One round control in a media card's corner (X's ✕ / edit chips). */
function CardChip({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "absolute z-10 flex h-7 min-w-7 items-center justify-center gap-1 rounded-full bg-black/70 px-1.5 text-[12px] font-semibold text-white backdrop-blur-md transition-transform duration-150 ease-out-strong active:scale-90",
        className,
      )}
    >
      {children}
    </button>
  );
}

function MediaCard({
  item,
  sizes,
  onRemove,
  onTag,
  onEdit,
}: {
  item: ComposerMediaItem;
  sizes: string;
  onRemove: () => void;
  onTag?: () => void;
  onEdit?: () => void;
}) {
  return (
    <>
      <div className="absolute inset-0 overflow-hidden bg-surface-alt">
        <MediaFill item={item} sizes={sizes} />
      </div>
      {item.isUploading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/35">
          <Loader2 className="size-6 animate-spin text-white" />
        </div>
      ) : null}
      {item.error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/60 px-3 text-center text-white">
          <AlertCircle className="size-5" />
          <span className="line-clamp-2 text-[12px] font-medium">
            {item.error}
          </span>
        </div>
      ) : null}
      <CardChip
        label={`Remove ${item.label}`}
        onClick={onRemove}
        className="top-2 right-2"
      >
        <X className="size-4" strokeWidth={2.5} />
      </CardChip>
      {/* The feed puts people tags bottom-left — so does the draft. */}
      {item.canTag && onTag ? (
        <CardChip
          label={item.tagCount > 0 ? "Edit tagged people" : "Tag people"}
          onClick={onTag}
          className="bottom-2 left-2"
        >
          <UserRound className="size-3.5" strokeWidth={2.25} />
          {item.tagCount > 0 ? <span>{item.tagCount}</span> : null}
        </CardChip>
      ) : null}
      {item.provider === "r2" && onEdit ? (
        <CardChip
          label="Edit media"
          onClick={onEdit}
          className="right-2 bottom-2"
        >
          <PenLine className="size-3.5" strokeWidth={2.25} />
        </CardChip>
      ) : null}
    </>
  );
}

/**
 * X's media: one item is a rounded frame at its own (clamped) shape in the
 * content column; several are a free-scrolling strip of rounded cards at one
 * height, bleeding to the screen's right edge. Mirrors `Post`'s media block.
 */
export function ComposerMediaBlock({
  items,
  onRemove,
  onTag,
  onEdit,
}: {
  items: ComposerMediaItem[];
  onRemove: (id: string) => void;
  onTag: (id: string) => void;
  onEdit: () => void;
}) {
  if (items.length === 0) return null;

  if (items.length === 1) {
    const [item] = items;
    const aspect = clampFeedMediaAspectRatio(
      item.width && item.height ? item.width / item.height : undefined,
    );
    return (
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-border"
        style={{ aspectRatio: `${aspect} / 1` }}
      >
        <MediaCard
          item={item}
          sizes="(max-width: 768px) 100vw, 520px"
          onRemove={() => onRemove(item.id)}
          onTag={() => onTag(item.id)}
          onEdit={onEdit}
        />
      </div>
    );
  }

  return (
    <div className={COMPOSER_COLUMN_BLEED}>
      <div
        className={cn(
          "no-scrollbar flex gap-2 overflow-x-auto overscroll-x-contain",
          COMPOSER_COLUMN_PAD,
        )}
      >
        {items.map((item) => {
          const itemAspect =
            item.width && item.height ? item.width / item.height : 0.8;
          return (
            <div
              key={item.id}
              className="relative h-[240px] max-w-[78vw] shrink-0 overflow-hidden rounded-2xl border border-border sm:max-w-[400px]"
              style={{
                aspectRatio: `${Math.min(1.5, Math.max(0.6, itemAspect))} / 1`,
              }}
            >
              <MediaCard
                item={item}
                sizes="320px"
                onRemove={() => onRemove(item.id)}
                onTag={() => onTag(item.id)}
                onEdit={onEdit}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The song over photos, as the post credits it ("♫ Title · Artist", the
 * `MediaMusicCredit` line): tap to change, ✕ to remove.
 */
export function ComposerMusicLine({
  music,
  onChange,
  onRemove,
}: {
  music: MusicAttachment;
  onChange: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="-my-1 flex min-w-0 items-center gap-1">
      <button
        type="button"
        onClick={onChange}
        aria-label={`${music.title} by ${music.artistName} — change song`}
        className="flex min-w-0 items-center gap-1.5 py-1 text-left text-[13px] leading-[18px] text-text-primary transition-opacity hover:opacity-75"
      >
        <span className="flex size-3.5 shrink-0 items-center justify-center">
          <Music2 className="size-3.5" />
        </span>
        <span className="truncate">
          {music.title}
          <span className="text-text-secondary"> · {music.artistName}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove song"
        className="flex size-7 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors pointer-fine:hover:bg-surface-alt pointer-fine:hover:text-text-primary"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/** X's brand-coloured action rows under the media ("👤 Tag people"). */
export function ComposerBrandRow({
  icon,
  children,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-my-1 flex w-fit max-w-full items-center gap-2 py-1 text-[14px] font-medium text-brand transition-opacity active:opacity-70"
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="truncate">{children}</span>
    </button>
  );
}

export type PollOptionDraft = {
  id: string;
  label: string;
};

export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 6;

/**
 * The poll as the post shows it (`PostPoll`: question over bordered option
 * rows), with every line editable in place.
 */
export function ComposerPollEditor({
  question,
  onQuestionChange,
  options,
  onOptionsChange,
  onRemove,
  createOption,
  disabled,
}: {
  question: string;
  onQuestionChange: (value: string) => void;
  options: PollOptionDraft[];
  onOptionsChange: (
    update: (previous: PollOptionDraft[]) => PollOptionDraft[],
  ) => void;
  onRemove: () => void;
  createOption: () => PollOptionDraft;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-border bg-surface-alt/40 p-2.5">
      <label className="block">
        <span className="sr-only">Poll question</span>
        <input
          value={question}
          onChange={(event) => onQuestionChange(event.target.value)}
          maxLength={280}
          placeholder="Ask a question…"
          disabled={disabled}
          className="w-full bg-transparent text-[15px] leading-snug font-semibold text-text-primary outline-none placeholder:font-medium placeholder:text-text-tertiary"
        />
      </label>

      <div className="space-y-1.5">
        {options.map((option, index) => (
          <label
            key={option.id}
            className="flex min-h-9 items-center rounded-lg border border-border bg-background transition-colors focus-within:border-brand"
          >
            <span className="sr-only">Poll option {index + 1}</span>
            <input
              value={option.label}
              onChange={(event) =>
                onOptionsChange((previous) =>
                  previous.map((item) =>
                    item.id === option.id
                      ? { ...item, label: event.target.value }
                      : item,
                  ),
                )
              }
              maxLength={80}
              placeholder={
                index < MIN_POLL_OPTIONS
                  ? `Choice ${index + 1}`
                  : `Choice ${index + 1} (optional)`
              }
              disabled={disabled}
              className="h-9 min-w-0 flex-1 bg-transparent px-2.5 text-sm font-medium text-text-primary outline-none placeholder:font-normal placeholder:text-text-tertiary"
            />
            {options.length > MIN_POLL_OPTIONS ? (
              <button
                type="button"
                onClick={() =>
                  onOptionsChange((previous) =>
                    previous.filter((item) => item.id !== option.id),
                  )
                }
                disabled={disabled}
                aria-label={`Remove choice ${index + 1}`}
                className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:text-text-primary"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </label>
        ))}
      </div>

      {/* X's poll footer: add a choice on the left, remove on the right —
          after the choices, so the tab order runs question → choices. */}
      <div className="flex min-h-7 items-center justify-between gap-2">
        {options.length < MAX_POLL_OPTIONS ? (
          <button
            type="button"
            onClick={() =>
              onOptionsChange((previous) => [...previous, createOption()])
            }
            disabled={disabled}
            className="flex items-center gap-1 text-sm font-semibold text-brand transition-opacity active:opacity-70 disabled:opacity-50"
          >
            <Plus className="size-4" />
            Add choice
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="text-sm font-medium text-error transition-opacity active:opacity-70 disabled:opacity-50"
        >
          Remove poll
        </button>
      </div>
    </div>
  );
}

/** Toolbar icon button: brand glyph, 40px target, optically equal sizes. */
export function ComposerToolButton({
  active = false,
  children,
  label,
  onClick,
  disabled,
}: {
  active?: boolean;
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-full text-brand transition-[background-color,transform,opacity] duration-150 ease-out-strong",
        "active:scale-90 active:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-35 pointer-fine:hover:bg-brand/10",
        active && "bg-brand/12",
      )}
    >
      {children}
    </button>
  );
}

/**
 * X's GIF glyph — a rounded box with the letters — drawn on lucide's 24px grid
 * with its stroke, so it sits level with the other toolbar icons.
 */
export function GifGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="2.5" y="5" width="19" height="14" rx="3" />
      <path d="M9.5 9.75h-1.5a1.5 1.5 0 0 0-1.5 1.5v1.5a1.5 1.5 0 0 0 1.5 1.5h1.5v-2.25h-1" />
      <path d="M12.25 9.75v4.5" />
      <path d="M17.5 9.75h-2.5v4.5M15 12h2" />
    </svg>
  );
}

export const CAPTION_LIMIT = 2000;

/** X's character ring: fills toward the limit, counts down near it. */
export function CharRing({
  value,
  max = CAPTION_LIMIT,
}: {
  value: number;
  max?: number;
}) {
  const remaining = max - value;
  const near = remaining <= 200;
  const over = remaining < 0;
  // X grows the ring when the countdown appears so the number fits.
  const size = near ? 28 : 22;
  const stroke = 2;
  const radius = size / 2 - stroke;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(1, value / max);

  return (
    <span
      className="relative flex shrink-0 items-center justify-center transition-[width,height] duration-150"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${remaining} characters remaining`}
    >
      <svg
        className="-rotate-90"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-border"
        />
        {over ? null : (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - ratio)}
            className={cn(
              "text-brand transition-[stroke-dashoffset]",
              near && "text-warning",
            )}
          />
        )}
      </svg>
      {near ? (
        <span
          className={cn(
            "absolute text-[10px] font-medium tabular-nums",
            over ? "text-error" : "text-text-secondary",
          )}
        >
          {remaining}
        </span>
      ) : null}
    </span>
  );
}
